import { AdminMicrostories } from '@platform/client';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { AdminMicrostory, SimplifiedUserRole } from '@platform/client/schemas';
import { PillBadge } from '@platform/components';
import { useCallback, useContext, useMemo, useState } from 'react';
import { CreateMicrostoryModal } from './CreateMicrostoryModal';
import { UpdateMicrostoryModal } from './UpdateMicrostoryModal';
import { DeleteMicrostoryModal } from './DeleteMicrostoryModal';
import { AdminActionsContainer, AdminListPageWrapper } from '../../AdminListPage';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { formatEnumLabel } from './formatEnumLabel';

const columnHelper = createColumnHelper<AdminMicrostory>();
const MICROSTORY_USER_TYPES = [SimplifiedUserRole.PROFESSIONAL, SimplifiedUserRole.REPRESENTATIVE];

const MicrostoriesAdmin = () => {
  return (
    <AdminListPageWrapper>
      <MicrostoriesAdminContent />
    </AdminListPageWrapper>
  );
};

const MicrostoriesAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [isCreateMicrostoryOpen, setIsCreateMicrostoryOpen] = useState(false);
  const [openMicrostory, setOpenMicrostory] = useState<AdminMicrostory | undefined>();
  const [microstoryToDelete, setMicrostoryToDelete] = useState<number | undefined>();

  const { data, isLoading, isError, isSuccess } = AdminMicrostories.useGetAdminMicrostories(
    adminQueryOptionsV2,
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const { microstories = [], filteredCount = 0 } = data || {};
  const { data: filterOptions } = AdminMicrostories.useGetMicrostoryFilterOptions();

  const openMicrostoryModal = useCallback(
    (microstoryId: number) => {
      setOpenMicrostory(microstories.find((microstory) => microstory.id === microstoryId));
    },
    [microstories],
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          header: () => <span>Id</span>,
          cell: (id) => id.getValue(),
          size: 50,
        }),
        columnHelper.accessor('contactName', {
          id: 'name',
          header: () => <span>Contact Name</span>,
          cell: (info) => info.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 175,
        }),
        columnHelper.accessor('organizationName', {
          id: 'organization',
          header: () => <span>Organization</span>,
          cell: (info) => info.getValue(),
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
          size: 175,
        }),
        columnHelper.accessor('microstory', {
          id: 'story',
          header: () => 'Story',
          cell: (info) => {
            const story = info.getValue();
            return story.length > 150 ? `${story.substring(0, 150)}...` : story;
          },
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
          size: 350,
        }),
        columnHelper.accessor('userType', {
          header: () => <span>User Type</span>,
          cell: (info) => {
            const isProfessional = info.getValue() === SimplifiedUserRole.PROFESSIONAL;
            return (
              <PillBadge variant='plain' color={isProfessional ? 'blue' : 'success-25'} size='sm'>
                {formatEnumLabel(info.getValue().toLowerCase())}
              </PillBadge>
            );
          },
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: MICROSTORY_USER_TYPES.map((value) => ({
              label: formatEnumLabel(value.toLowerCase()),
            })),
          },
          enableSorting: false,
          size: 150,
        }),
        columnHelper.accessor('corporateClientName', {
          id: 'corporateClient',
          header: () => <span>Corporate Client</span>,
          cell: (info) =>
            info.getValue() ?
              <PillBadge variant='plain' color='orange' size='sm'>
                {info.getValue()}
              </PillBadge>
            : <span className='text-gray-400'>&mdash;</span>,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: (filterOptions?.corporateClients ?? []).map((client) => ({
              label: client.name,
            })),
          },
          enableSorting: false,
          size: 175,
        }),
        columnHelper.accessor('impactPartnerName', {
          header: () => <span>Impact Partner</span>,
          cell: (info) =>
            info.getValue() ?
              <PillBadge variant='plain' color='indigo' size='sm'>
                {info.getValue()}
              </PillBadge>
            : <span className='text-gray-400'>&mdash;</span>,
          enableSorting: false,
          enableColumnFilter: false,
          size: 175,
        }),
        columnHelper.accessor('storyDate', {
          header: () => <span>Date</span>,
          cell: (info) => new Date(info.getValue()).toLocaleDateString(),
          meta: {
            filterVariant: 'date-select',
          },
          size: 125,
        }),
        columnHelper.accessor('source', {
          header: () => <span>Source</span>,
          cell: (info) => formatEnumLabel(info.getValue()),
          enableSorting: false,
          size: 150,
        }),
        columnHelper.display({
          id: 'Actions',
          header: () => <span>Actions</span>,
          size: 25,
          cell: ({ row }) => (
            <AdminActionsContainer
              actions={[
                {
                  text: 'Edit',
                  type: 'button',
                  onPress: () => openMicrostoryModal(row.original.id),
                },
                {
                  text: 'Delete',
                  type: 'button',
                  onPress: () => setMicrostoryToDelete(row.original.id),
                  linkColor: 'red',
                },
              ]}
            />
          ),
        }),
      ] as ColumnDef<AdminMicrostory, unknown>[],
    [openMicrostoryModal, filterOptions],
  );

  return (
    <>
      <AdminTable
        data={microstories}
        columns={columns}
        filteredCount={filteredCount}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
        enableFiltering={true}
        tableActions={[
          {
            callback: () => setIsCreateMicrostoryOpen(true),
            text: 'Create Microstory',
          },
        ]}
      />
      {isCreateMicrostoryOpen && (
        <CreateMicrostoryModal onClose={() => setIsCreateMicrostoryOpen(false)} />
      )}
      {openMicrostory && (
        <UpdateMicrostoryModal
          microstory={openMicrostory}
          onClose={() => setOpenMicrostory(undefined)}
        />
      )}
      {microstoryToDelete && (
        <DeleteMicrostoryModal
          isOpen={!!microstoryToDelete}
          onClose={() => setMicrostoryToDelete(undefined)}
          id={microstoryToDelete}
        />
      )}
    </>
  );
};

export default MicrostoriesAdmin;
